/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/AttendanceHistory.tsx
 * PURPOSE: "Lịch sử" (chấm công) — web-sync checklist A.4. Unlocks the
 *          EmployeeDashboard "Lịch sử" quick-link tile AND the "Tôi" (Profile)
 *          menu's "Lịch sử chấm công" item.
 *          2026-09-04 (mobile-app-shell parity pass): this route previously
 *          carried its own ~200-line duplicate of the OT-summary-card +
 *          record-list body, independently drifting from the SAME body that
 *          AttendanceModern.tsx's in-flow `history` screenState now renders
 *          via the shared `HistoryContent` component (extracted 2026-09-02,
 *          see that file's header — "Trích nguyên vẹn từ AttendanceHistory.tsx
 *          — KHÔNG đổi hành vi, chỉ tách phần thân ra khỏi khung trang"). That
 *          extraction's own stated intent was for THIS file to delegate to it
 *          ("route độc lập `/attendance/history`... vẫn giữ nguyên làm lối
 *          vào trực tiếp") — never completed until now. This file now only
 *          supplies the page chrome (480px app-shell header + EmployeeBottomNav)
 *          and renders <HistoryContent>; the OT-summary/record-list markup,
 *          timezone helpers, and resolution-log rendering all live in
 *          HistoryContent.tsx (frontend/src/features/attendanceModern/
 *          screens/HistoryContent.tsx) — single source, no drift risk.
 *          `fmtDate`/`fmtTime` are re-exported here (not duplicated) purely
 *          so the existing standalone regression test
 *          (AttendanceHistory.timezone.test.ts, imports from '@/pages/
 *          AttendanceHistory') keeps passing unchanged.
 *          Header itself moved from the old max-width:1400px two-row layout
 *          (title + separate "Về Dashboard" pill) to the single-row
 *          ‹-back + title app-shell header already proven on
 *          AttendanceModern.module.css/.tsx — see AttendanceHistory.module.css
 *          same-date changelog entry.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-04
 * COMPLIANCE: SDLC 6.5.0 + Sprint Governance Rule 3 (Zero-Mock Tolerance)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Employee Self-Service, Attendance, Vietnamese F&B
 * - Impact: User Experience — employees can see past attendance records
 * - Stakeholders: Employees (71)
 *
 * @technical-architecture
 * - Layer: Presentation Layer, Page Component (chrome only — body delegated)
 * - Pattern: TanStack Query (via HistoryContent → useSelfAttendanceHistory), CSS Modules
 * - Dependencies: React 18, react-router-dom, lucide-react
 *
 * @cross-references
 * - Body (shared, not duplicated): src/features/attendanceModern/screens/HistoryContent.tsx
 * - In-flow sibling consumer: src/pages/AttendanceModern.tsx (screenState 'history')
 * - Hook (reused via HistoryContent): src/hooks/useManagerProfile.ts (useSelfAttendanceHistory, useSelfOtSummary)
 * - Unlocked from: src/pages/dashboards/EmployeeDashboard.tsx (quickLinks.history) +
 *   src/pages/manager/ManagerProfilePage.tsx-style "Tôi" menu (Lịch sử chấm công)
 * - Header pattern mirrored from: src/pages/AttendanceModern.tsx (headerBackButton/headerTitleWrap)
 * - Timezone helpers test: frontend/tests/pages/AttendanceHistory.timezone.test.ts
 * - OT-summary/resolution-log tests: frontend/tests/pages/AttendanceHistory.test.tsx,
 *   frontend/tests/pages/AttendanceHistory.resolutionLog.test.tsx (still pass unchanged —
 *   same data-testids, now rendered by HistoryContent instead of inline here)
 *  */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { HistoryContent } from '@/features/attendanceModern/screens/HistoryContent';
import { EmployeeBottomNav } from '@/components/navigation/EmployeeBottomNav';
import styles from './AttendanceHistory.module.css';

// Re-exported (not duplicated) so the pre-existing standalone regression test
// (AttendanceHistory.timezone.test.ts, imports `fmtDate`/`fmtTime` from
// '@/pages/AttendanceHistory') keeps passing without modification. The real
// implementation lives in HistoryContent.tsx — this file no longer has its
// own copy.
export { fmtDate, fmtTime } from '@/features/attendanceModern/screens/HistoryContent';

export const AttendanceHistory: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <button
            type="button"
            onClick={() => navigate('/dashboard/staff')}
            className={styles.headerBackButton}
            aria-label="Quay lại"
            data-testid="attendance-history-header-back"
          >
            <ChevronLeft size={24} aria-hidden="true" />
          </button>
          <div className={styles.headerTitleWrap}>
            <h1 id="attendance-history-title" className={styles.title}>Lịch sử chấm công</h1>
          </div>
        </div>
      </header>

      <main aria-labelledby="attendance-history-title" className={styles.main}>
        <HistoryContent onClockInCta={() => navigate('/attendance')} />
      </main>

      <EmployeeBottomNav />
    </div>
  );
};

export default AttendanceHistory;
